import {
  Box, Button, DialogActions, DialogContent, DialogTitle, Grid,
  Typography, Avatar, Autocomplete, Stack, Tooltip, Modal, TextField, Divider, IconButton, InputAdornment, Chip, FormControlLabel,
  CircularProgress, ToggleButton, ToggleButtonGroup, MenuItem
} from '@mui/material';
import { makeStyles } from "@material-ui/core/styles";
import React, { FC, useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector, } from "../../store/hooks";
import { StyledInput } from './Dialog.styles';
import { fetchAccountsByName, fetchBuyerOrSupplierByName, searchUnofyCrm, getUnofyCrmDetail } from "../../store/accountThunk";
import CustomTextField from '../FormFields/CustomTextField';
import CustomTextField1 from '../FormFields/CustomTextField1';
import Address from './ClientAgent/Address';
import Legal from './ClientAgent/Legal';
import Payment from './ClientAgent/Payment';
import Bank from './ClientAgent/Bank';
import { FormProvider, useForm } from 'react-hook-form';
import { IAccountBasicFormInputs } from "../../utils/account.types";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { ApiStatus } from '../../utils/api.types';
import LoadingButton from '@mui/lab/LoadingButton';
import { addBuyerSellerAccount } from "../../store/accountThunk";
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { fetchSuppliers } from '../../store/supplierStore/supplierThunk';
import { fetchCustomers } from '../../store/customerStore/customerThunk';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import EmailChip from '../EmailChip/EmailChip';
import { toast } from 'react-toastify';
import DialogClientUpdateForm from './UpdateClientForm';
import useModuleAccess from '../../hooks/useModuleAccess';

interface DialogClientFormProps {
  title?: string;
  open: boolean;
  onClose: (k: any) => void;
  onCancel: () => void;
  selectedData: (data: any) => void;
  /** Address of the voucher's branch, used when the CRM lead has none of its own. */
  locationAddress?: any;
}

// Country and state are seeded by the form itself, so only the typed fields count
// as evidence that an address was actually supplied.
const ADDRESS_FIELDS = ['Line1', 'Line2', 'City', 'PinCode'];

const hasAnyAddress = (values: any) =>
  ['billingAddress', 'shippingAddress'].some((prefix) =>
    ADDRESS_FIELDS.some((f) => String(values?.[`${prefix}${f}`] || '').trim() !== '')
  );

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 1000,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
};

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    boxShadow: 'none',
    borderRadius: 7
  },
  toggleButtonGroup: {
    width: '100%',
  },
  toggleButton: {
    width: 15,
    height: 15,
    marginBottom: 2
  },

});

const DialogClientForm: FC<DialogClientFormProps> = ({ title, onClose, onCancel, open, selectedData, locationAddress, children }) => {
  // On when the CRM sent no address at all: the branch address stands in, shown as
  // placeholders, so the required-address rules would block a form the user need
  // not fill. Any address data from either side turns this off.
  const [useLocationAddress, setUseLocationAddress] = useState(false);
  const addressRule = (message: string) =>
    useLocationAddress ? yup.string().notRequired() : yup.string().required(message);

  // Schema to validate account form.
  const schema = yup.object().shape({
    name: yup.string().required("Name is required."),
    legalName: yup.string().required("Legal Name is required."),
    billingAddressCountry: addressRule("Billing Address Country is required."),
    billingAddressCity: addressRule("Billing Address City is required."),
    shippingAddressCountry: addressRule("Shipping Address Country is required."),
    shippingAddressCity: addressRule("Shipping Address City is required."),
    // registrationNo: yup.string().required("Registration is required."),
    // countryOfRegistration: yup.string().required("Country of registration is required."),
    // stateOfRegistration: yup.string().required("State of registration is required."),
    // taxIdentificationNumber1: yup.string().required("Tax Identification #1 (PAN for India) is required."),
    // taxIdentificationNumber2: yup.string().required("Tax Identification #2 (GST for India) is required."),
    // upiId: (title === 'Supplier') ? yup.string().required("UPI ID is required.") : yup.string().notRequired(),
    // gPayPhone: (title === 'Supplier') ? yup.string().required("GPay number is required.") : yup.string().notRequired(),
    // bankName: (title === 'Supplier') ? yup.string().required("Bank name is required.") : yup.string().notRequired(),
    // branchName: (title === 'Supplier') ? yup.string().required("Branch name is required.") : yup.string().notRequired(),
    // IFSC: (title === 'Supplier') ? yup.string().required("IFSC is required.") : yup.string().notRequired(),
    currency: (title === 'Supplier') ? yup.string().required("Supplier is required.") : yup.string().notRequired(),
    // accountNumber: (title === 'Supplier') ? yup.string().required("Account number is required.") : yup.string().notRequired(),
    // accountName: (title === 'Supplier') ? yup.string().required("Account name is required.") : yup.string().notRequired()
  });

  const dispatch = useAppDispatch();
  const classes = useStyles();
  const [image, setImage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [options, setOptions] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>();
  const [newOption, setNewOption] = useState("");
  // MUI Select needs its own value; register() alone won't display it.
  const [gender, setGender] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [saveButton, setSaveButton] = useState('Select');
  const { selectedAccount } = useAppSelector(state => state.auth);
  const elmRef = useRef<any>(null);
  const [mailerList, setMailerList] = useState<any>();
  const [isClientEdit, setIsClientEdit] = useState<any>();
  // Edit the selected buyer / supplier in place, without leaving the voucher form.
  const [openEditParty, setOpenEditParty] = useState(false);
  const { can } = useModuleAccess();
  const canEditParty = can(title === 'Supplier' ? 'suppliers' : 'buyers', 'edit');
  // Reload the edited party so the fields (and the voucher) get the saved values.
  const reloadEditedParty = async (name: string) => {
    const id = selected?._id;
    if (!id || !name) return;
    try {
      const data: any = await dispatch(fetchBuyerOrSupplierByName(`name=${encodeURIComponent(name)}&&searchFor=${title?.toLocaleLowerCase()}&limit=10`)).unwrap();
      const fresh = (data?.accounts || []).find((a: any) => a._id === id);
      if (fresh) setSelected(fresh);
    } catch (error) {
      console.error('Error reloading edited party:', error);
    }
  };
  
  const [searchTerm, setSearchTerm] = useState('');   // Track user input
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm); // Debounced value
  const TIME_DELAY = 500;

  // Unofy CRM buyer import. Only offered for Buyers on accounts with the flag on.
  const crmEnabled = title === 'Buyer' && !!selectedAccount?.unofyCrm?.enabled;
  const [searchSource, setSearchSource] = useState<'wareozo' | 'unofy'>('wareozo');
  // Provenance of a CRM-imported buyer, carried into the create payload.
  const [crmImport, setCrmImport] = useState<{
    leadId: string; source: string; sourceName: string; subSourceName: string; agentName: string;
  } | null>(null);

  // use form
  const methods = useForm<IAccountBasicFormInputs>({
    resolver: yupResolver(schema),
  });
  const formRef = useRef<any>();

  const { status } = useAppSelector(state => state.account);

  // Method to set preview of selected picture.
  const handleSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files[0]) {
      setSelectedFile(target.files[0]);
      setImage(URL.createObjectURL(target.files[0]));
    }
  };
  const onChangeHandler = (event: any, value: any) => {
    console.log("onChangeHandler called", value);
    if (value && value.__crm) {
      importCrmLead(value);
      return;
    }
    setNewOption('');
    // setIsLoading(true);
    setSelected(value);
    setIsClientEdit(false);
    setUseLocationAddress(false);
    // setIsLoading(false);
  }

  // CRM provenance lives on the invoice, not on the party record, so it travels
  // on the buyer object handed back to the invoice form.
  const withCrmProvenance = (buyer: any) => {
    if (!buyer || !crmImport) return buyer;
    return {
      ...buyer,
      crmLeadId: crmImport.leadId,
      crmSource: crmImport.source,
      crmSourceName: crmImport.sourceName,
      crmSubSourceName: crmImport.subSourceName,
      crmAgentName: crmImport.agentName,
    };
  };

  const importCrmLead = async (row: any) => {
    const leadId = row.leadId;
    try {
      setIsLoading(true);
      const res: any = await dispatch(getUnofyCrmDetail(leadId)).unwrap();
      const form = res?.buyerForm || {};

      // The attribution comes off the search row — the detail call doesn't return it.
      setCrmImport({
        leadId: form.leadId || leadId,
        source: form.source || '',
        sourceName: row.source_name || '',
        subSourceName: row.sub_source_name || '',
        agentName: row.agent_name || '',
      });

      // Already a buyer here: load it the way a Wareozo pick does, so the user
      // gets Select instead of Create and no duplicate party is made.
      if (res?.existingBuyer) {
        setNewOption('');
        setIsClientEdit(false);
        setSaveButton('Select');
        setSelected(res.existingBuyer);
        setUseLocationAddress(false);
        return;
      }

      // Fresh lead: enter "Add New" (create) mode without a `selected`.
      setSelected('');
      setIsClientEdit(true);
      setSaveButton('Create');
      setNewOption('new');

      // Prefill the form. reset() first so stale values are cleared, then apply.
      methods.reset(form as any);
      // A CRM DOB always wins — a CRM age goes stale as time passes.
      const crmDob = form.dateOfBirth || '';
      methods.setValue('dateOfBirth', crmDob);
      methods.setValue('age', calculateAge(crmDob) || (form.age ?? ''));
      // The Gender select is controlled by local state, so reset() alone won't show it.
      methods.setValue('gender', form.gender || '');
      setGender(form.gender || '');
      // EmailChip reads its initial value from mailerList; seed it from the CRM emails.
      setMailerList(Array.isArray(form.email) ? form.email : (form.email ? [form.email] : []));
      setUseLocationAddress(!hasAnyAddress(form) && !!locationAddress);
    } catch (error) {
      console.error('Error importing CRM lead:', error);
    } finally {
      setIsLoading(false);
    }
  };
  // Update the debounced value after a delay (debouncing the user input)
  useEffect(() => {
    if (selected && searchTerm === selected.name) return; // picked buyer's name, not typing
    setIsLoading(true);
    const timerId = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim().length >= 3 ? searchTerm : ''); // search from 3 chars
      setIsLoading(false);
    }, 500);  // 500ms delay
    
    return () => clearTimeout(timerId); // Cleanup previous timer on new keypress
  }, [searchTerm]);

  // Make API call when debouncedTerm changes (user has stopped typing)
  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        if (crmEnabled && searchSource === 'unofy') {
          // Unofy CRM lead search. Requires a term (CRM has no "empty = recent" mode).
          if (!debouncedTerm) {
            setOptions([]);
          } else {
            const qs = `q=${encodeURIComponent(debouncedTerm)}&type=lead&page=1&limit=20`;
            await dispatch(searchUnofyCrm(qs)).unwrap().then((data) => {
              const rows = (data && data.data) ? data.data : [];
              // Shape rows so getOptionLabel/renderOption (which read option.name) work.
              setOptions(rows.map((r: any) => ({
                ...r,
                name: r.name || r.phone || r.leadId,
                __crm: true,
              })));
            });
          }
        } else {
          const _querystring = `name=${debouncedTerm}&&searchFor=${title?.toLocaleLowerCase()}&limit=10`
          await dispatch(fetchBuyerOrSupplierByName(_querystring)).unwrap().then((data) => {
            if (data) {
              const d = data.accounts.filter(function (item) {
                return item._id !== selectedAccount._id;
              });

              // When no search term, show first 5 results
              setOptions(debouncedTerm ? d : d.slice(0, 5));
            }
          });
        }
      } catch (error) {
        console.error("Error fetching search results:", error);
        setOptions([]);
      }
      setTimeout(() => {setIsLoading(false)}, TIME_DELAY);
    };

    fetchData();
  }, [debouncedTerm, searchSource]); // Run when debouncedTerm or source changes

  const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
    setSearchTerm(value);
  };

  // const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
  //   setSelected('');
  //   setNewOption('');
  //   const { _id: id } = selectedAccount;
  //   // let option = [];
  //   // const data: any = await dispatch(fetchCustomers(id));
  //   // console.log(data)

  //   // if (data.payload.length > 0) {
      
  //   //   option = data.payload.map((item: any) => {
  //   //     console.log(item);
  //   //     return { label: item.name, value: item._id }
  //   //   });
  //   // }
  //   // console.log("setting up options");
  //   // console.log(option);
  //   // setOptions(option);


  //   // await dispatch(fetchCustomers(id)).unwrap().then((data) => {
  //   //   console.log(data)
  //   //   if (data) {
  //   //     console.log("checking customer data");
  //   //     console.log(data.customers);
  //   //     let option = [];
  //   //     option = data.customers.filter((item: any) => {
  //   //       console.log(item);
  //   //       return { label: item.name, value: item._id }
  //   //     });

  //   //     console.log(option);

  //   //     setOptions(option);

  //   //     // const d = data.customers.filter(function (item) {
  //   //     //   return item._id !== selectedAccount._id;
  //   //     // });

  //       setOptions(option);
  //   //   }
  //   // });
  // // };

  // const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
  //   setSelected('');
  //   setNewOption('');
  //   console.log("input changed called");
  //   if (selectedAccount) {
  //     const { _id: id } = selectedAccount;
  //     const data: any = await dispatch(fetchCustomers(id));
  //     console.log(data);

  //     if (data.payload.length > 0) {
  //       let option = [];
  //       option = data.payload.map((item: any) => {
  //         console.log(item);
  //         return { label: item.name, value: item._id }
  //       });
  //       setOptions(option);
  //     }
  //   }
  // };

  const onAddNew = () => {
    if (elmRef) {
      const ele = elmRef?.current.getElementsByClassName('MuiAutocomplete-clearIndicator')[0];
      if (ele) ele.click();
    }
    // Manual add is not a CRM import — clear any prior CRM prefill provenance.
    setCrmImport(null);
    setUseLocationAddress(false);
    setIsClientEdit(true);
    setSelected('')
    setNewOption('');
    setSaveButton('Create')
    setNewOption('new');
  };

  useEffect(() => {
    // A CRM import prefills the form itself (importCrmLead) — don't wipe it here.
    if (crmImport && newOption === 'new') {
      setIsLoading(false);
      return;
    }
    methods.reset();
    if (selected) {
      methods.setValue("name", selected.name);
      methods.setValue("legalName", selected.legalName);
      methods.setValue("website", selected.website);
      methods.setValue("contactName", selected.contactName);
      methods.setValue("email", selected.email);
      methods.setValue("whatsAppNumber", selected.whatsAppNumber);
      if (title == 'Buyer') {
        // <input type="date"> needs a YYYY-MM-DD value.
        const dob = (selected as any).dateOfBirth ? String((selected as any).dateOfBirth).slice(0, 10) : '';
        methods.setValue("dateOfBirth", dob);
        // A stored DOB always wins — a saved age goes stale as time passes.
        methods.setValue("age", calculateAge(dob) || ((selected as any).age ?? ''));
        methods.setValue("gender", (selected as any).gender || '');
        setGender((selected as any).gender || '');
      }
      setIsLoading(false);
    } else if (newOption) {
      setGender('');
      setIsLoading(false);
    }
  }, [selected, newOption, methods, isClientEdit, crmImport]);

  // Completed years from a YYYY-MM-DD string; '' when it isn't a usable past date.
  const calculateAge = (value: string): number | '' => {
    if (!value) return '';
    const dob = new Date(value);
    if (isNaN(dob.getTime())) return '';
    const today = new Date();
    let years = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) years--;
    return years >= 0 ? years : '';
  };

  // DOB drives age; age stays editable for parties with no DOB.
  const handleDobChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    methods.setValue('dateOfBirth', value);
    const years = calculateAge(value);
    if (years !== '') methods.setValue('age', years);
  };

  const getFormData = (object: any) => {
    const formData = new FormData();
    Object.keys(object).forEach(key => formData.append(key, object[key]));
    return formData;
  };

  const createClient = async (e: any) => {
    if (selectedFile) {
      e.append('logo', selectedFile);
    }

    e.delete('email');
    e.append('email', JSON.stringify(mailerList));

    const resData: any = await dispatch(addBuyerSellerAccount(e));

    // Surface any backend rejection (e.g. duplicate name/PAN/GST) and keep the
    // dialog open so the user can correct or search for the existing party.
    if (resData.error) {
      toast.error(resData.payload?.message || 'Failed to create buyer.');
      return;
    }

    if (resData.payload) {
      const buyer = resData.payload.fullAccountDetails;
      // Attach the external CRM provenance to the buyer handed back to the
      // invoice, so it can be stored on the invoice (not on the party record).
      setSelected(buyer);
      selectedData(withCrmProvenance(buyer));
      onClose(true);
      methods.reset();
    }
  };

  const handleRenderOption = (e: any, b: any, c: any) => {
    // console.log('b.counterPartyOfAccount: ', b.counterPartyOfAccount, b.name);
    return isLoading ? <div style={{ 'marginLeft': '20px' }}><CircularProgress color="info" size={20}/></div> : <div {...e} className='auto-item' key={Math.random()}>
      <div className='img-wrapper'>
        <Avatar
          alt={b.name}
          imgProps={{ referrerPolicy: "no-referrer" }}
          src={b.logo}
          variant='square'
          style={{
            width: "30px",
            height: "30px",
            objectFit: "contain",
          }}
          sizes={"xs"}
        >{b.name.toString().substr(0, 1).toUpperCase()}</Avatar>
      </div>
      <div><b style={{ 'marginRight': '4px' }}>
        {b.name}
      </b>
        {b.__crm
          ? <>
              {/* One person can hold several leads, so the row has to be identifiable. */}
              <span style={{ fontSize: '11px', color: '#488dff', fontWeight: 600 }}>{b.leadId}</span>
              <div style={{ fontSize: '11px', color: '#888' }}>
                {[b.phone, b.lead_age, b.facility_name, b.sub_source_name, b.agent_name,
                  b.createdAt ? new Date(b.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''
                ].filter(Boolean).join(' • ')}
              </div>
            </>
          : <>
              {!b.counterPartyOfAccount && <Tooltip title="Verified account"><InfoIcon style={{ fontSize: '12px', color: '#09b2ee' }}></InfoIcon></Tooltip>}
              {b.counterPartyOfAccount && <Tooltip title="My customer / vendor"><InfoIcon style={{ fontSize: '12px', color: '#aaa' }}></InfoIcon></Tooltip>}
              {b.whatsAppNumber && <div style={{ fontSize: 12, color: '#64748b' }}>{b.whatsAppNumber}</div>}
            </>
        }
      </div>
    </div>
  };

  const selectedEmailDataHandler = (data: any) => {
    console.log("====> selectedEmailDataHandler called from updateClientForm  ====> ");
    setMailerList(data);
    console.log(mailerList);
  };

  const onSubmit = (e: any) => {

    const data = methods.getValues();
    data.buyerSellerAgent = title;
    data.email = mailerList;

    // The placeholders were only a hint; nothing typed means the branch address is
    // what gets saved. One field typed and the user owns the whole address.
    if (useLocationAddress && locationAddress && !hasAnyAddress(data)) {
      ['billingAddress', 'shippingAddress'].forEach((prefix) => {
        (data as any)[`${prefix}Line1`] = locationAddress.line1 || '';
        (data as any)[`${prefix}Line2`] = locationAddress.line2 || '';
        (data as any)[`${prefix}City`] = locationAddress.city || '';
        (data as any)[`${prefix}State`] = locationAddress.state?._id || locationAddress.state || '';
        (data as any)[`${prefix}Country`] = locationAddress.country?._id || locationAddress.country || '';
        (data as any)[`${prefix}PinCode`] = locationAddress.code || '';
      });
    }
    console.log(data);

    delete e.search;
    e.buyerSellerAgent = title;

    let form = getFormData(data);
    createClient(form);
  };

  return (
    <div className="drawer-form-container">
      <Modal
          open={open}
          onClose={(event : any, reason:any) => {
              if (reason !== 'backdropClick') {
                onClose(false);
              }
          }}
      >
      <FormProvider {...methods}>
          <Box sx={style} component="form" onSubmit={methods.handleSubmit(data => onSubmit(data))} ref={formRef} noValidate>
            <Grid container>
                <Grid {...{ xs: 12, sm: 8, md: 4, lg: 12, p:2, pl:2, bgcolor:'#e9ecf1' }} style={{borderTopLeftRadius:8,borderTopRightRadius:8,height:50}} >
                <div style={{float:'right',marginTop:-7,marginRight:-5}}>
                    
                  {selected && selected._id && canEditParty &&
                    <Button
                      style={{color:'#488dff',backgroundColor:'#fff',border:'1px solid #488dff', marginRight:10 }}
                      variant="contained"
                      size='small'
                      onClick={() => setOpenEditParty(true)} > Edit </Button>
                  }
                  {selected &&
                    <Button 
                      style={{color:'#488dff',backgroundColor:'#fff',border:'1px solid #488dff', marginRight:10 }}
                      variant="contained"
                      size='small'
                      onClick={() => {
                        methods.reset();
                        selectedData(withCrmProvenance(selected));
                        setSelected('');
                        setNewOption('');
                        setSaveButton('Select')
                        onClose(false);
                      }} > Select </Button>
                  }
                  {newOption == 'new' && 
                    <LoadingButton
                      type="submit"
                      name={'submit'}
                      // disabled={!methods.formState.isValid}
                      loading={status === ApiStatus.LOADING}
                      style={{color:'#488dff',backgroundColor:'#fff',border:'1px solid #488dff', marginRight:10 }}
                      variant="contained"
                      size='small'
                    > {saveButton} </LoadingButton> 
                  }

                  <Button
                    style={{color:'#488dff',backgroundColor:'#fff',border:'1px solid #488dff', marginRight:10 }}
                    variant="contained"
                    size='small'
                    onClick={() => {
                      methods.reset();
                        setSelected('');
                        setNewOption('');
                        onCancel();
                        onClose(false);
                    }}>
                    Cancel
                  </Button>
                    {/* <IconButton size='small' 
                      onClick={() => {
                        methods.reset();
                        setSelected('');
                        setNewOption('');
                        onCancel();
                        onClose(false);
                      }}
                      style={{marginRight:-5}} 
                      >
                      <HighlightOffOutlinedIcon  color="error"/>
                    </IconButton> */}
                </div>
                <h3  style={{marginTop:-2}}>Search {title}</h3>
                
                </Grid>
                
            </Grid>
            <Grid container spacing={2} rowSpacing={1} p={2} height={'90vh'} overflow={'auto'}>

              {/* {JSON.stringify(methods.formState.errors) || {}} */}

              <Grid item xs={8} >
                {crmEnabled &&
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={searchSource}
                    onChange={(_e, val) => {
                      if (!val) return;
                      setSearchSource(val);
                      // Reset search context when switching source.
                      setSearchTerm('');
                      setDebouncedTerm('');
                      setOptions([]);
                      setSelected('');
                      setNewOption('');
                      setCrmImport(null);
                                    }}
                    style={{ marginBottom: 6 }}
                  >
                    <ToggleButton value="wareozo" style={{ textTransform: 'none' }}>Wareozo</ToggleButton>
                    <ToggleButton value="unofy" style={{ textTransform: 'none' }}>Unofy CRM</ToggleButton>
                  </ToggleButtonGroup>
                }
                <h5 className='c-label2'>{searchSource === 'unofy' ? 'Search Unofy CRM (lead)' : `Search ${title}`}</h5>
                <Autocomplete
                  ref={elmRef}
                  id={`search ${title}`}
                  disablePortal
                  onChange={onChangeHandler}
                  inputValue={searchTerm}
                  onInputChange={onInputChangeHandler}
                  options={options}
                  filterOptions={(x) => x}
                  renderOption={handleRenderOption}
                  classes={{ paper: classes.paper }}
                  sx={{ width: '100%'}}
                  getOptionLabel={option => option.name || ''}
                  renderInput={(params) => <TextField {...params}
                  placeholder={searchSource === 'unofy' ? 'Search CRM by name or phone' : `Type here to Search ${title}`}
                    required
                    className='c-text-field'
                    variant="standard"
                    size='medium'
                    InputProps={{...params.InputProps, disableUnderline: true}}
                  />}
                />
              </Grid>

              <Grid item xs={4} >
              {!selected && 
                <Button variant='contained' size='small' onClick={onAddNew}
                  style={{ marginTop: '18px', color:'#488dff',backgroundColor:'#fff',border:'1px solid #488dff', float:'right' }} >
                    Add New {title}
                </Button>
              }
              {/* <Tooltip title="Add new"><AddIcon style={{ cursor: 'pointer', marginTop: '16px' }} onClick={onAddNew} fontSize="medium" /></Tooltip> */}
              </Grid>

              <Grid item xs={12} mb={1}>
                <Divider/>
              </Grid>

              {isLoading ? <div></div> : (selected || (newOption)) && (
                <>
                  <Grid item xs={2} >
                        <Avatar
                          alt={"Picture"}
                          imgProps={{ referrerPolicy: "no-referrer" }}
                          src={(selected && selected.logo) ? selected.logo : image}
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "contain",
                            marginBottom:10,
                            border:'1px dashed #9da7b7',
                            marginLeft:15
                          }}
                          variant='rounded'
                          sizes={"lg"}
                        />
                      {!selected && <label htmlFor="logo">
                        <StyledInput
                          accept="image/*"
                          id="logo"
                          type="file"
                          onChange={handleSelectFile}
                        />
                        <Button
                        style={{marginLeft:15, width:'120px', color:'#488dff',backgroundColor:'#fff',border:'1px dashed #488dff' }}
                          variant="contained"
                          component="span"
                          size='small'
                        >
                          Choose Logo
                        </Button>
                      </label>}
                  </Grid>
                  <Grid item xs={10}>
                  <Grid container spacing={2} rowSpacing={1}>

                  <Grid item xs={7.2} >
                  <h5 className='c-label'>Name*</h5>
                    <TextField
                    className='c-text-field' 
                    {...methods.register('name')}
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      readOnly: (selected) ? true : false
                    }}/>
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.name?.message ?? ""}</p>
                  </Grid>
                  <Grid item xs={4.8}>
                  <h5 className='c-label'>Legal Name *</h5>
                    <TextField
                    placeholder='legalName' 
                    required 
                    className='c-text-field' 
                    {...methods.register('legalName')}
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      readOnly: (selected) ? true : false
                    }}/> 
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.legalName?.message ?? ""}</p>
                  </Grid>
                  <Grid item xs={7.2}>
                  <h5 className='c-label'>Website</h5>
                    <TextField
                    placeholder='website' 
                    className='c-text-field' 
                    {...methods.register('website')}
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      readOnly: (selected) ? true : false
                    }}/>
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.website?.message ?? ""}</p>
                    </Grid>

                  <Grid item xs={4.8}>
                  <h5 className='c-label'>Contact Name</h5>
                    <TextField
                    placeholder='Contact Name' 
                    className='c-text-field'
                    {...methods.register('contactName')}
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      readOnly: (selected) ? true : false
                    }}/>
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.contactName?.message ?? ""}</p>
                  </Grid>
                  <Grid item xs={7.2}>
                    {/* <CustomTextField label="Email" name="email" variant="standard" InputProps={{ readOnly: (selected) ? true : false }} /> */}
                    <EmailChip buyerEmail={selected ? selected.email : mailerList} methods={methods} selectedEmailData={selectedEmailDataHandler} isEdit={isClientEdit}/>
                  </Grid>
                  <Grid item xs={4.8}>
                  <h5 className='c-label'>WhatsApp Number</h5>
                    <TextField
                    placeholder='WhatsApp Number' 
                    type='number'
                    className='c-text-field'
                    {...methods.register('whatsAppNumber')}
                    variant="standard" 
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      readOnly: (selected) ? true : false
                    }}/>
                    <p style={{color: "#c33b3b"}}>{methods.formState.errors.whatsAppNumber?.message ?? ""}</p>
                  </Grid>
                  {title == 'Buyer' && <>
                  <Grid item xs={4}>
                  <h5 className='c-label'>Date of Birth</h5>
                    <TextField
                    type='date'
                    className='c-text-field'
                    {...methods.register('dateOfBirth')}
                    onChange={handleDobChange}
                    variant="standard"
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      readOnly: (selected) ? true : false
                    }}/>
                  </Grid>
                  <Grid item xs={4}>
                  <h5 className='c-label'>Age</h5>
                    <TextField
                    placeholder='Age'
                    type='number'
                    className='c-text-field'
                    {...methods.register('age')}
                    variant="standard"
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      readOnly: (selected) ? true : false
                    }}/>
                  </Grid>
                  <Grid item xs={4}>
                  <h5 className='c-label'>Gender</h5>
                    <TextField
                    select
                    className='c-text-field'
                    {...methods.register('gender')}
                    value={gender}
                    onChange={(event) => {
                      setGender(event.target.value);
                      methods.setValue('gender', event.target.value);
                    }}
                    variant="standard"
                    sx={{width:'100%'}}
                    size='small'
                    InputProps={{
                      disableUnderline: true,
                      readOnly: (selected) ? true : false
                    }}>
                    <MenuItem value=''>Select Gender</MenuItem>
                    <MenuItem value='Male'>Male</MenuItem>
                    <MenuItem value='Female'>Female</MenuItem>
                    <MenuItem value='Other'>Other</MenuItem>
                  </TextField>
                  </Grid>
                  </>}
                  </Grid>
                  </Grid>
                  <Address selected={selected} methods={methods} fallbackAddress={useLocationAddress ? locationAddress : null} />

                  {title == 'Supplier' &&
                  <>
                  <Grid item xs={6} mt={1}>
                    <Divider style={{marginRight:-8}}>
                      <Chip label="UPI Details " size="small" />
                    </Divider>
                  </Grid>
                  <Grid item xs={6} mt={1}>
                    <Divider style={{marginLeft:-8}}>
                      <Chip label="Bank Details " size="small" />
                    </Divider>
                  </Grid>
                  <Grid item xs={6.1} pr={2}>
                    <Grid container spacing={2} rowSpacing={1} pt={-2}>
                      <Payment selected={selected} methods={methods} />
                    </Grid>
                  </Grid>
                  <Grid item xs={5.9} style={{borderLeft:'1px solid rgba(0, 0, 0, 0.12)'}}>
                    <Grid container spacing={2} rowSpacing={1} >
                      <Bank selected={selected} methods={methods} />
                    </Grid>
                  </Grid>
                  </>
                  }

                  <Legal selected={selected} methods={methods} />
                  {/* {title === 'Supplier' && <Payment selected={selected} methods={methods} />}
                  {title === 'Supplier' && <Bank selected={selected} methods={methods} />} */}
                </>
              )}
            </Grid>
            {/* <Grid container gap='0' sx={{ mr: '32px', pt: '10px' }}>
            <Grid item xs={4} display="flex" justifyContent="flex-end" >
                <Button variant='outlined'
                  style={{ marginRight: '10px' }}
                  onClick={() => {
                    methods.reset();
                    onCancel();
                    onClose(false);
                  }}>Cancel</Button>
                {selected ?
                  <Button variant='contained'
                    style={{ marginRight: '10px' }}
                    onClick={() => {
                      methods.reset();
                      selectedData(selected);
                      onClose(false);
                    }}>Select</Button> :
                  <LoadingButton
                    type="button"
                    onClick={onSubmit}
                    // disabled={!methods.formState.isValid}
                    loading={status === ApiStatus.LOADING}
                    variant="contained"
                  >
                    {saveButton}
                  </LoadingButton>}
              </Grid>
            </Grid> */}
        </Box>
      </FormProvider>
      </Modal>
      {openEditParty && (
        <DialogClientUpdateForm
          title={title}
          open={openEditParty}
          onClose={() => setOpenEditParty(false)}
          onCancel={() => setOpenEditParty(false)}
          selectedData={selected}
          isClientEdit={true}
          onSaved={reloadEditedParty}
        />
      )}
    </div >
    
  );
};

export default DialogClientForm;
